import React from "react";
import { type GetStaticProps, type NextPage } from "next";
import {
  type Page as SanityPage,
  type Banner,
  type Customer,
  getAnnouncementBanner,
  getPage,
  getFeaturedCustomers,
} from "lib/sanity";
import { getPageMetaImage } from "utils";
import { HalfHero } from "components/half-hero";
import { Section, sectionSpacingY } from "components/section";
import { Page } from "components/page";
import { CenteredQuote } from "components/quotes";
import { Customers } from "components/logos";
import { DemoEmbedHalf } from "components/demo-embed";
import { PairImage } from "components/pair-image";
import { RadialGradient } from "ui/radial-gradient";
import { CustomerStats, SecurityFeatures } from "components/content";
import { AuraPlate } from "ui/plates";
import { integrationsCount } from "../../constants/integrations";
import heroImage from "../../../public/assets/solutions/media/hero.png";
import firstPairImage from "../../../public/assets/solutions/media/pair-1.png";
import secondPairImage from "../../../public/assets/solutions/media/pair-2.png";
import thirdPairImage from "../../../public/assets/solutions/media/pair-3.png";

type PageProps = {
  page: SanityPage;
  announcementBanner?: Banner;
  featuredStatCustomers: Customer[];
};

const Media: NextPage<Readonly<PageProps>> = ({ page, announcementBanner, featuredStatCustomers }) => {
  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
    >
      <HalfHero
        hideSecondary
        eyebrow={page.eyebrow}
        title={page.title}
        description={page.description}
        image={heroImage}
        imageRatio={3 / 2}
        imageObjectFit="contain"
        backgroundGradient={
          <>
            <RadialGradient
              variant="mint"
              bottom="84%"
              right={{ base: "50%", md: "24%" }}
              boxSize={{ base: "70%", md: "80%" }}
              opacity={{ base: 0.4, xl: 0.6 }}
            />

            <RadialGradient
              variant="cyan"
              bottom={{ base: "70%", md: "6%" }}
              right={{ base: "-50%", md: "-120%" }}
              boxSize={{ base: "120%", md: "160%" }}
              opacity={{ base: 0.6, xl: 0.7 }}
            />
          </>
        }
      />

      {page.customers && <Customers customers={page.customers} my={16} />}

      <CustomerStats customers={featuredStatCustomers} />

      <DemoEmbedHalf />

      <Section>
        <PairImage
          align="right"
          image={firstPairImage}
          title="Earn more from advertising placements"
          description="Drive more revenue by enhancing the targeting of your retail media placements with your first-party data. Always serve the most relevant content at the right time."
        />
      </Section>

      <Section>
        <PairImage
          align="left"
          image={secondPairImage}
          title="Hyper-personalize customer journeys"
          description={`Leverage your customer data to personalize every customer interaction across your website, email, ads, and ${integrationsCount}+ other Hightouch destinations. Media recommendations made easy.`}
        />
      </Section>

      <Section>
        <PairImage
          align="right"
          image={thirdPairImage}
          title="Centralize audience management"
          description="Build and sync audiences to any marketing tool or ad platform with a no-code audience builder. Target your most valuable customers across channels. No CSV uploads required."
          actionText="Explore Customer Studio"
          actionUrl="/platform/customer-studio"
        />
      </Section>

      <AuraPlate condensed variant="blue" py={0}>
        <CenteredQuote
          quoteText="The combination of Snowflake and Hightouch forms the cornerstone of our customer intelligence platform."
          author={{
            name: "Tom Dinneny",
            job: "Customer Intelligence Platform Director",
          }}
          company={{
            name: "Warner Music Group",
            logo: "https://cdn.sanity.io/images/pwmfmi47/production/84dbb616e064de970801b02e16e419d60fcd4b44-2560x1009.png",
            logoRatio: 3 / 1,
          }}
          cta={{ href: "/customers/wmg", text: "Read customer story" }}
        />
      </AuraPlate>

      <SecurityFeatures my={0} py={sectionSpacingY} />
    </Page>
  );
};

export const getStaticProps: GetStaticProps<PageProps> = async () => {
  const [page, announcementBanner, featuredStatCustomers] = await Promise.all([
    getPage("/solutions/media"),
    getAnnouncementBanner(),
    getFeaturedCustomers(["wmg", "auto-trader", "minno"]),
  ]);

  if (!page) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      page,
      announcementBanner,
      featuredStatCustomers,
    },
    revalidate: 60,
  };
};

export default Media;
